package dev.lingvo.site.pages.app.settings.billing

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import dev.lingvo.shared.Plan
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.ds.LAsyncContent
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.dateLong
import dev.lingvo.site.ds.formatCount
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LSectionTitle
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.state.WorkspaceActionClaim
import dev.lingvo.site.state.WorkspacePrivilege
import dev.lingvo.site.state.WorkspaceRequestContext
import dev.lingvo.site.state.runWorkspaceAction
import dev.lingvo.site.pages.app.settings.loadWorkspaceProtectedResource
import dev.lingvo.site.pages.app.settings.rememberWorkspaceAuthorityState
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf

/**
 * Cancelling, on a page that answers the question people actually have.
 *
 * Which is never "are you sure" — it is "what happens to my work". So this states the answer before
 * the button: nothing is deleted, the export keeps working, and the date it takes effect.
 *
 * **Owners only, and not because admins are untrusted.** A Paddle subscription belongs to the person
 * who bought it, and an admin ending it would end a contract they are not party to.
 *
 * A retention offer would go here in most products. There isn't one: somebody who came to this page
 * has decided, and a discount interstitial is a tax on that decision.
 */
@Page("/app/settings/billing/cancel")
@Layout(".components.layouts.AppLayout")
@Composable
fun CancelSubscriptionPage() {
    val ctx = rememberPageContext()
    val workspaceContext = WorkspaceRequestContext.current()
    val ownerClaim = WorkspaceActionClaim.capture(workspaceContext, WorkspacePrivilege.OWNER_BILLING)
    var busy by rememberWorkspaceAuthorityState(workspaceContext) { mutableStateOf(false) }
    var message by rememberWorkspaceAuthorityState(workspaceContext) { mutableStateOf<String?>(null) }
    var done by rememberWorkspaceAuthorityState(workspaceContext) { mutableStateOf(false) }

    // A retry that retries. `ErrorState` draws its button whenever `onRetry` is non-null, so
    // `{ }` produced a visible control that does nothing — on a page reached precisely when
    // the network is unreliable, whose only other exit is a full browser reload.
    var refreshKey by rememberWorkspaceAuthorityState(workspaceContext) { mutableStateOf(0) }

    Column(Modifier.fillMaxWidth().gap(Space.XL)) {
        PageHeader(t("app.settings.billing.cancel.cancel_the_subscription", "Cancel the subscription"), t("app.settings.billing.cancel.what_happens_before_you_decide", "What happens, before you decide."))

        if (ownerClaim == null) {
            LAlert(
                t("app.settings.billing.cancel.only_an_owner_can_do_this", "Only an owner can do this"),
                t("app.settings.billing.cancel.a_subscription_belongs_to_the_person_2", "A subscription belongs to the person who bought it. Ask an owner to cancel, or to " +
                    "make you one."),
                variant = LAlertVariant.INFO,
            )
            return@Column
        }

        val state = loadWorkspaceProtectedResource(
            context = workspaceContext,
            privilege = WorkspacePrivilege.OWNER_BILLING,
            requestKeys = listOf(refreshKey),
            transport = ApiClient::workspaceBilling,
        )

        when {

            done -> LCard(Modifier.fillMaxWidth()) {
                Column(Modifier.gap(Space.SM)) {
                    LSectionTitle(t("app.settings.billing.cancel.cancelled", "Cancelled."))
                    LMuted(t("app.settings.billing.cancel.the_workspace_stays_on_its_current", "The workspace stays on its current plan until the period ends, then moves to Free. Nothing was deleted."))
                    LButton(t("app.settings.billing.cancel.back_to_billing", "Back to billing"), { ctx.router.tryRoutingTo("/app/settings/billing") })
                }
            }

            else -> LAsyncContent(
                state,
                rows = 2,
                loadingLabel = t("app.settings.billing.cancel.loading_the_subscription", "Loading the subscription"),
                onRetry = { refreshKey++ },
            ) { billing ->
                if (billing == null || !billing.hasSubscription) {
                    LAlert(
                        t("app.settings.billing.cancel.there_is_nothing_to_cancel", "There is nothing to cancel"),
                        t("app.settings.billing.cancel.this_workspace_is_on_the_free", "This workspace is on the Free plan."),
                        variant = LAlertVariant.INFO,
                    )
                } else {
                    LCard(Modifier.fillMaxWidth()) {
                        Column(Modifier.fillMaxWidth().gap(Space.MD)) {
                            LSectionTitle(t("app.settings.billing.cancel.what_happens", "What happens"))
                            Ul {
                                // `date/long`, not the raw ISO this used to print. The date a
                                // plan ends is the single fact this page exists to state, and
                                // "2026-09-03" is a machine's shape for it — read as prose it
                                // stops a reader mid-sentence to decode a field they already
                                // know. An unparseable date falls to the wording below rather
                                // than putting a fourth date shape back on the page.
                                Fact(
                                    cancellationPlanFact(
                                        plan = billing.plan.displayName,
                                        endDate = billing.renewsAt?.let(::dateLong),
                                    ),
                                )
                                // The fear, answered first and plainly.
                                Fact(t("app.settings.billing.cancel.nothing_is_deleted_every_project_string", "Nothing is deleted. Every project, string, translation, comment and version stays where it is."))
                                Fact(t("app.settings.billing.cancel.export_keeps_working_including_the_full", "Export keeps working, including the full archive with your memory, glossary and history."))
                                // Computed, not recited. This page's whole job is answering
                                // "what happens to my work", and the honest answer depends on
                                // this workspace's numbers: a six-admin Studio loses 30,000
                                // credits a month and a solo Free-curious one loses 4,750.
                                // A constant sentence is wrong for both — and for the big
                                // workspace it was silent about the only thing that stops
                                // working, while warning the small one about read-only limits
                                // that will never bind it.
                                //
                                // "Included" is load-bearing: a grant is counted separately and
                                // lapses with the month it was made for, so it is not part of
                                // what a plan change takes away.
                                val freeCredits = Plan.FREE.creditsFor(billing.seats.held)
                                // Separated and dated. "from 30000 to 250" is the figure a
                                // customer is weighing against a card charge, and an
                                // unseparated five-digit run is read a digit at a time — the
                                // one place on this page where misreading a number by a factor
                                // of ten changes the decision.
                                Fact(
                                    cancellationCreditsFact(
                                        from = formatCount(billing.creditsIncluded),
                                        to = formatCount(freeCredits),
                                        endDate = billing.renewsAt?.let(::dateLong),
                                    ),
                                )
                                Fact(cancellationSeatsFact(billing.seats.held))
                                Fact(t("app.settings.billing.cancel.machine_translation_with_your_own_provider", "Machine translation with your own provider key keeps working and still costs no credits."))
                            }

                            // `live`, because this banner only ever appears in answer to the
                            // button below it. A cancellation that failed silently is a customer
                            // who believes the subscription is ended and stops looking.
                            message?.let {
                                LAlert(
                                    t("app.settings.billing.cancel.that_didnt_work", "That didn't work"),
                                    it,
                                    variant = LAlertVariant.WARNING,
                                    live = true,
                                )
                            }

                            // Equal weight. A giant "Keep my plan" next to a grey "Cancel" is a
                            // dark pattern with better manners.
                            Row(Modifier.gap(Space.SM)) {
                                CancelSubscriptionAction(
                                    pageClaim = ownerClaim,
                                    busy = busy,
                                    setBusy = { busy = it },
                                    onCancelled = {
                                        done = true
                                        message = null
                                    },
                                    onFailure = { error ->
                                        message = error.message?.takeIf(String::isNotBlank)
                                            ?: cancellationRefused()
                                    },
                                )
                                LButton(
                                    t("app.settings.billing.cancel.keep_it", "Keep it"),
                                    { ctx.router.tryRoutingTo("/app/settings/billing") },
                                    variant = LButtonVariant.SECONDARY,
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
internal fun CancelSubscriptionAction(
    pageClaim: WorkspaceActionClaim,
    busy: Boolean,
    setBusy: (Boolean) -> Unit,
    currentContext: () -> WorkspaceRequestContext? = WorkspaceRequestContext::current,
    cancel: suspend (workspaceSlug: String) -> Unit = ApiClient::cancelWorkspaceSubscription,
    launchAction: ((suspend () -> Unit) -> Unit)? = null,
    onCancelled: () -> Unit,
    onFailure: (Throwable) -> Unit,
) {
    val scope = rememberCoroutineScope()
    val launch: (suspend () -> Unit) -> Unit = launchAction ?: { action ->
        scope.launch { action() }
    }

    LButton(
        t("app.settings.billing.cancel.cancel_the_subscription", "Cancel the subscription"),
        {
            val actionClaim = pageClaim
                .takeIf { it.remainsAuthorized(currentContext()) }
                ?: return@LButton
            launch {
                runWorkspaceAction(
                    claim = actionClaim,
                    currentContext = currentContext,
                    setBusy = setBusy,
                    transport = cancel,
                    onSuccess = { onCancelled() },
                    onFailure = onFailure,
                )
            }
        },
        enabled = !busy,
        loading = busy,
        variant = LButtonVariant.SECONDARY,
    )
}

@Composable
private fun Fact(text: String) {
    Li { Span(Modifier.fontSize(TypeScale.BodySmall).toAttrs()) { Text(text) } }
}

/**
 * The three consequence sentences on this page, each one whole message.
 *
 * Two of the five facts were catalogued and three were raw Kotlin templates, on the page that
 * decides whether somebody keeps paying. The seat sentence was the worst of them: half went
 * through `tPlural` and the rest was glued on with `+`, so a German reader got "Sie halten 3
 * Owner- und Admin-Plätze. Free includes 1. Nobody is demoted…" — one sentence in two languages,
 * in an order no locale could change. `trf`/`tPlural`, because these are plain functions.
 */
internal fun cancellationPlanFact(plan: String, endDate: String?): String = endDate
    ?.let {
        trf(
            "app.settings.billing.cancel.you_keep_plan_until_date",
            "You keep {plan} until {date}, then move to Free.",
            "plan" to plan,
            "date" to it,
        )
    }
    ?: trf(
        "app.settings.billing.cancel.you_keep_plan_until_the_period_ends",
        "You keep {plan} until the period ends, then move to Free.",
        "plan" to plan,
    )

/** What the included monthly credits drop to, and when. Both numbers are already separated. */
internal fun cancellationCreditsFact(from: String, to: String, endDate: String?): String = endDate
    ?.let {
        trf(
            "app.settings.billing.cancel.monthly_credits_included_for_the_shared",
            "Monthly credits included for the shared translation key drop from {from} to {to} on {date}. Credits granted for this month lapse with the period.",
            "from" to from,
            "to" to to,
            "date" to it,
        )
    }
    ?: trf(
        "app.settings.billing.cancel.monthly_credits_drop_when_the_period",
        "Monthly credits included for the shared translation key drop from {from} to {to} when the period ends. Credits granted for this month lapse with the period.",
        "from" to from,
        "to" to to,
    )

/** One key for the whole seat sentence, so the count and the clause it governs travel together. */
internal fun cancellationSeatsFact(seatsHeld: Int): String = tPlural(
    "app.settings.billing.cancel.count.owner_admin_seat_and_free_limit",
    seatsHeld,
    "You hold {count} owner and admin seat. Free includes 1. Nobody is demoted or removed, but you won't be able to promote anyone else until you're back under that limit.",
    "You hold {count} owner and admin seats. Free includes 1. Nobody is demoted or removed, but you won't be able to promote anyone else until you're back under that limit.",
)

/**
 * What a failed cancellation says.
 *
 * The highest-consequence mutation in billing, on a page whose every other string was catalogued.
 */
internal fun cancellationRefused(): String = tr(
    "app.settings.billing.cancel.couldnt_cancel_this_subscription_retry",
    "Couldn't cancel this subscription. Retry?",
)
